import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { FaSteam } from "react-icons/fa";

import { FollowButton } from "../../../Components/Button";
import { ESuggestedFollowReason, ISuggestedFollower } from "../@types";
import { useMutateFollowers } from "../../../Hooks/useMutateFollowers";
import { TMutateFollowers } from "../../../Hooks/@types";
import { AllstarModalState } from "../../../State/modals";
import { ModalType } from "../../../@types";
import { SuggestedFollowing } from "MVVM/State/suggestedFollowing";
import { useUser } from "MVVM/Hooks/useUser";
import { SUGGESTED_REASON_CONFIG } from "./reasonConfig";
import { SuggestionBadge } from "../../../Components/SuggestionBadge";
import { Avatar } from "MVVM/Components/AvatarV2";

interface ISuggestedItemProps {
  user: ISuggestedFollower;
  reason: ESuggestedFollowReason;
}

const SuggestedItem = ({ user, reason }: ISuggestedItemProps) => {
  const { allstarUser } = useUser();
  const [suggestedFollowing, setSuggestedFollowing] =
    useRecoilState(SuggestedFollowing);
  const history = useHistory();
  const { mutateFollowers } = useMutateFollowers();

  const [allstarModalState, setAllstarModalState] =
    useRecoilState(AllstarModalState);
  const followUnfollow = (data: TMutateFollowers) => {
    setSuggestedFollowing({
      ids: [...suggestedFollowing.ids, data?.variables?.userToFollow],
    });

    return mutateFollowers(data);
  };
  const setFollowModal = ({
    displayName,
    avatar,
  }: {
    displayName: string;
    avatar: string;
  }) => {
    setAllstarModalState({
      ...allstarModalState,
      isOpen: ModalType.Signup,
      data: {
        target: displayName,
        avatar: avatar,
        action: "follow",
      },
    });
  };

  const suggestionItem =
    SUGGESTED_REASON_CONFIG[reason] || SUGGESTED_REASON_CONFIG["Unspecified"];

  const handleUserClick = () => {
    if (!user?.username) return;

    const location =
      !user.username || user.username === "User"
        ? `/profile?user=${user._id}`
        : `/u/${user.username}`;

    history.push(location);

    window.scroll({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  };

  const username =
    (user.username !== "User" ? user.username : user?.steam?.personaname) ??
    "User";

  const isFollowBackButton =
    suggestionItem === SUGGESTED_REASON_CONFIG["Follow Back"];

  return (
    <SuggestedItemWrapper>
      <SuggestedItemContainer>
        <SuggestionBadge reason={reason} showTooltip={false} />
        <AvatarClickable onClick={handleUserClick}>
          <Avatar
            userGroup={user.userGroup?.type || "FREE"}
            rank={user.rank}
            src={user.avatar}
            size="lg"
          />
        </AvatarClickable>
        <TextWrapper onClick={handleUserClick}>
          <UsernameText fontSize="4">{username}</UsernameText>
          <SteamUsername>
            {user?.steam?.personaname ? (
              <>
                <FaSteam />
                <SteamUsernameText>{user.steam.personaname}</SteamUsernameText>
              </>
            ) : (
              <>&nbsp;</>
            )}
          </SteamUsername>
        </TextWrapper>

        <FollowButton
          followBack={isFollowBackButton}
          size="tiny"
          flex={true}
          openModal={true}
          isModal={false}
          followUnfollow={followUnfollow}
          setFollowModal={setFollowModal}
          userId={user._id}
          allstarUser={allstarUser}
          displayName={user.username}
          avatar={user.avatar}
          trackingInfo={{
            avatarPresent: !!user.avatar,
            discordPresent: !!user?.discord?.id,
            followingCount: allstarUser?.user?.followingCount,
            reason: user.reason,
          }}
        />
      </SuggestedItemContainer>
    </SuggestedItemWrapper>
  );
};

const AvatarClickable = styled(Box)`
  cursor: pointer;
`;

const SuggestedItemWrapper = styled(Box)`
  max-width: 220px;
  width: 220px;
  overflow: visible;
`;

const SuggestedItemContainer = styled(Flex)`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  padding: 12px 24px 
  width: auto;
  min-height: 280px;
  overflow: visible;
`;

const TextWrapper = styled(Flex)`
  flex-direction: column;
  color: ${({ theme }) => theme.colors.ice};
  font-weight: 700;
  outline: none;
  cursor: pointer;
  gap: 8px;
  justify-content: center;
  align-items: center;
  max-width: 160px;
  width: 100%;
`;

const UsernameText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align: center;
`;

const SteamUsername = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 12px;
  font-weight: 500;
  max-width: 160px;
  width: 100%;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SteamUsernameText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 16px);
`;

export { SuggestedItem };
